<template>
  <div class="batch-create">
    <b-loading :active="loading" />
    <div class="batch-create__action action">
      <router-link to="/member/list" class="action__prev-page">
        <i class="icon-chevron-left-bold" />
        返回人員管理
      </router-link>
      <h1 class="action__title">
        批次新增/更新人員
      </h1>

      <div class="box">
        <h2 class="box__header">
          <span class="box__header--title">
            下載匯入範本
            <b-dropdown position="is-bottom-right">
              <template #trigger="{ active }">
                <b-button type="is-primary" size="is-small">
                  下載範本
                  <i :class="{'icon-up-chevron': active, 'icon-down-chevron': !active}" />
                </b-button>
              </template>
              <b-dropdown-item>
                <a
                  href="/files/好彙訂-批次新增(更新)人員範例.csv"
                  download>
                  好彙訂-批次新增/更新人員範例.csv
                </a>
              </b-dropdown-item>
              <b-dropdown-item>
                <a
                  href="/files/好彙訂-批次新增(更新)人員範例.xlsx"
                  download>
                  好彙訂-批次新增/更新人員範例.xlsx
                </a>
              </b-dropdown-item>
            </b-dropdown>
          </span>
          <span class="box__header--title-action">
            需要幫助嗎？
            <a href="https://help.takeorder.tw/2022/04/01/%e4%bd%bf%e7%94%a8%e8%80%85%e6%93%8d%e4%bd%9c%e6%8c%87%e5%8d%97%ef%bc%9a%e6%89%b9%e6%ac%a1%e6%96%b0%e5%a2%9e%e4%ba%ba%e5%93%a1/" target="_blank">
              查看幫助中心 批次新增/更新人員指南
            </a>
          </span>
        </h2>
        <p class="box__content text-black">請下載匯入範本檔，再於檔案內新增人員名單後至下方批次新增/更新區塊內上傳。</p>
      </div>

      <div class="box">
        <h2 class="box__header">批次新增/更新</h2>
        <div class="box__content p-0 pb-4 has-text-centered">
          <b-upload
            v-model="file"
            accept=".csv,.xlsx"
            drag-drop
            expanded
          >
            <div class="upload__icon">
              <i class="icon-upload" />
            </div>
            <p class="upload__description">{{ file? file.name : '點擊此處選擇檔案或直接拖曳檔案至此' }}</p>
            <div class="upload__description--muted">
              <p>注意事項：</p>
              <p v-for="(notice, noticeIndex) in notices" :key="`notice-${noticeIndex}`">
                {{ noticeIndex + 1 }}. {{ notice }}
              </p>
            </div>
          </b-upload>
          <w-button
            :disabled="!file"
            class="upload__action"
            size="sm"
            @click="onUpload"
          >
            <i v-if="file" class="icon-upload" />
            上傳檔案
          </w-button>
        </div>
      </div>
    </div>
    <div class="batch-create__record record">
      <h3 class="record__title">
        紀錄
      </h3>
      <b-table
        :data="records"
        default-sort="import_time"
        default-sort-direction="desc"
        scrollable
      >
        <b-table-column
          :th-attrs="columnThAttrs"
          label="上傳時間"
          field="import_time"
          cell-class="text-black"
          width="160px"
          v-slot="props"
        >
          {{ getRecordDatetime(props.row.import_time).date }}
          <span class="ml-2">
            {{ getRecordDatetime(props.row.import_time).time }}
          </span>
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          label="處理結果"
          cell-class="record__result"
          width="300px"
          v-slot="props">
          <span
            :class="getResult(props.row).class">
            {{ getResult(props.row).label }}
          </span>
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          label="成功筆數"
          cell-class="record__description"
          v-slot="props"
        >
          <span v-if="props.row.status !== importingStatusMapping.COMPLETED">
            -
          </span>
          <span v-else-if="props.row.success_count">
            {{ props.row.success_count }}
          </span>
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          label="失敗筆數"
          cell-class="record__action"
          v-slot="props"
        >
          <template
            v-if="
              props.row.status === importingStatusMapping.COMPLETED &&
              props.row.fail_count
            "
          >
            <span class="text-red-500">
              {{ props.row.fail_count }}
            </span>
            <b-button
              outlined
              class="ml-4"
              size="is-small"
              type="is-primary"
              @click="onDownload(props.row.uuid)"
            >
              下載名單
            </b-button>
          </template>
        </b-table-column>

        <template v-if="!loading" #empty>
          <p>目前無批次新增/更新紀錄</p>
          <div class="my-6">
            <img class="mx-auto" src="@/assets/images/empty-importing-record.svg">
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapActions } from 'vuex';

import {
  MEMBER_IMPORTING_STATUS,
  SNACKBAR_TYPES,
} from '@/utils/constants';
import {
  IMPORT_MEMBERS,
  DOWNLOAD_IMPORTING_RESULT,
  GET_IMPORTING_RECORD,
} from '@/store/member/constants';

function CSVLengthError() {
  this.name = 'CSVLengthError';
}
CSVLengthError.prototype = new Error();
CSVLengthError.prototype.constructor = CSVLengthError;

export default {
  name: 'MemberBatchCreate',
  data() {
    return {
      loading: false,
      maxCSVContentLength: 10000,
      importingStatusMapping: MEMBER_IMPORTING_STATUS,
      file: null,
      notices: [
        '上傳檔案格式限 csv 或 xlsx',
        '批次上傳檔案最多可包含 10,000 筆資料',
        '若上傳資料中的帳號已存在，系統將依上傳資料更新該帳號。反之則將自動建立新帳號。',
        '新建立的帳號將自動啟用不需驗證。預設密碼為「a+公司統編」，例如：a54259841。',
      ],
      refreshingTimer: null,
      records: [],
    };
  },
  computed: {
    processingFile() {
      return this.records.some(
        ({ status }) => status !== MEMBER_IMPORTING_STATUS.COMPLETED,
      );
    },
  },
  async mounted() {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.loading = true;
      await this.getRecords();
      this.autoRefresh();
    } catch (ex) {
      const errorMessage = ex.message ? `(${ex.message})` : '';

      this.$snackbar({
        message: `載入紀錄時發生錯誤，請重試一次${errorMessage}`,
        type: SNACKBAR_TYPES.ERROR,
      });
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    clearInterval(this.refreshingTimer);
  },
  methods: {
    async getRecords() {
      this.records = await this.getImportingRecord();
    },
    autoRefresh() {
      if (!this.processingFile) {
        return;
      }
      this.refreshingTimer = setInterval(async () => {
        if (!this.processingFile) {
          clearInterval(this.refreshingTimer);
          this.$snackbar({
            title: '批次新增成功',
            msg: '批次新增成功且已發送驗證信',
            type: SNACKBAR_TYPES.SUCCESS,
          });
          return;
        }
        await this.getRecords();
      }, 10000);
    },
    columnThAttrs() {
      return {
        class: 'text-xs',
      };
    },
    getRecordDatetime(datetime) {
      const dateObject = dayjs(datetime);

      return {
        date: dateObject.format('YYYY/MM/DD'),
        time: dateObject.format('HH:mm'),
      };
    },
    getResult(record) {
      const results = {
        [MEMBER_IMPORTING_STATUS.PROCESSING]: {
          class: 'text-gray-500',
          label: '處理中',
        },
        [MEMBER_IMPORTING_STATUS.COMPLETED]: record?.fail_count ? {
          class: 'text-red-500',
          label: '部分成功',
        } : {
          class: 'text-black',
          label: '成功',
        },
        [MEMBER_IMPORTING_STATUS.FAILED]: {
          class: 'text-red-500',
          label: '失敗',
        },
      };

      return results[record?.status] || {
        class: 'text-black',
        label: '-',
      };
    },
    validateCSV() {
      const fileReader = new FileReader();
      const result = new Promise((resolve, reject) => {
        fileReader.onload = (e) => {
          const csv = e.target.result;
          const records = csv.split('\n');
          const headerLength = 5; // NOTE: The length of header in the example file

          if ((records.length - headerLength) > this.maxCSVContentLength) {
            reject(new CSVLengthError());
            return;
          }
          resolve();
        };
      });
      fileReader.readAsText(this.file);
      return result;
    },
    async onUpload() {
      try {
        this.loading = true;
        if (this.file?.type === 'text/csv') {
          await this.validateCSV();
        }
        await this.importMembers(this.file);
        await this.getRecords();
        this.autoRefresh();
        this.file = null;
      } catch (ex) {
        if (ex instanceof CSVLengthError) {
          this.$snackbar({
            type: SNACKBAR_TYPES.ERROR,
            title: '資料筆數超過10000筆',
            msg: '請刪減至10000筆以內',
          });
        } else {
          this.$snackbar({
            type: SNACKBAR_TYPES.ERROR,
            title: '上傳失敗',
            msg: ex.message || '發生未知錯誤，請再試一次',
          });
        }
      } finally {
        this.loading = false;
      }
    },
    async onDownload(uuid) {
      try {
        this.loading = true;
        this.downloadImportingResult({ uuid, filename: 'result.csv' });
      } catch (ex) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '下載失敗',
          msg: ex.message || '下載時發生未知錯誤，請再試一次',
        });
      } finally {
        this.loading = false;
      }
    },
    ...mapActions('member', {
      importMembers: IMPORT_MEMBERS,
      downloadImportingResult: DOWNLOAD_IMPORTING_RESULT,
      getImportingRecord: GET_IMPORTING_RECORD,
    }),
  },
};
</script>

<style lang="scss" scoped>
.batch-create {
  padding: 2rem 100px;
  @include apply-to-tablet {
    padding: 0 1rem;
  }
  @include apply-to-phone {
    padding: 0 .5rem;
  }

  &__action {
    padding: .875rem 1rem;
    border-radius: 2px;
    background-color: $white;
  }
  &__record {
    margin-top: 1.5rem;
    padding: 1rem;
    border-radius: 2px;
    background-color: $white;
  }
}

.action {
  &__prev-page {
    font-size: .875rem;
    font-weight: 500;
    color: $dark-grey;
    i {
      vertical-align: middle;
    }
    &:hover {
      color: $blue;
      i::before {
        color: $blue;
      }
    }
  }
  &__title {
    margin: 1rem 0 2rem 0;
    font-size: 1.25rem;
    font-weight: 500;
    color: $black;
    small {
      margin-left: 1rem;
      font-size: 1rem;
      font-weight: 400;
      color: $dark-grey;
    }
  }
}

.box {
  border-radius: 4px;
  box-shadow: 0px 0px 4px 1px $lighter-grey;
  padding: 0;
  &__header {
    @apply flex font-medium items-center justify-between text-xl;

    padding: .875rem 1.5rem;
    border-bottom: 1px solid $lighter-grey;

    &--title {
      .button {
        vertical-align: text-bottom;
        margin-left: 2rem;
        border-radius: 4px;
        i::before {
          color: $white;
        }
      }
      &-action {
        font-size: 1rem;
        font-weight: 400;
        a {
          color: $blue;
          &[target="_blank"]::after {
            font-family: '#{$icomoon-font-family}' !important;
            content: $icon-open-externally;
          }
        }
      }
    }
  }
  &__content {
    padding: 1rem 1.5rem;
  }
}

::v-deep .upload {
  &-draggable {
    border: none;
  }
  &__icon {
    margin: 1rem 0;
    i {
      font-size: 2.5rem;
    }
  }
  &__description {
    text-align: center;
    margin-bottom: .5rem;
    &--muted {
      @apply flex flex-col leading-5 mb-2 mx-auto text-left text-xs;
      color: $dark-grey;
      width: fit-content;
    }
  }
  &__action {
    margin-top: 1rem;
    font-size: .875rem;
    i::before {
      color: $white;
    }
  }
}

.record {
  &__title {
    font-size: 1.25rem;
    margin-bottom: .5rem;
    small {
      margin-left: .5rem;
      font-size: .875rem;
      color: $dark-grey;
    }
  }
  &__action {
    .button {
      border-radius: 4px;
    }
  }
  ::v-deep .b-table {
    box-shadow: 0px 0px 4px 1px $lighter-grey;
    transition: box-shadow 0.2s ease-in-out;
    border-radius: 4px;
    &:hover {
      box-shadow: 0px 0px 10px 1px $lighter-grey;
    }
    .table {
      thead th {
        height: 40px;
        line-height: 40px;
        font-size: .75rem;
        font-weight: 500;
        color: $black;
        padding: 0 .75rem;
        border: 1px solid map.get($custom-colors, 'light-grey-500');
        border-width: 0 0 1px;
        &.is-sortable {
          &:hover {
            border-color: map.get($custom-colors, 'grey-300');
          }
          &.is-current-sort {
            color: $green;
            border-color: map.get($custom-colors, 'grey-300');
          }
        }
      }
      tbody tr {
        height: 3rem;
        font-size: .875rem;
        td {
          vertical-align: middle;
        }
        &:not(:last-child) td {
          font-size: .875rem;
          border-bottom: 1px solid map.get($custom-colors, 'light-grey-500');
          transition: border-bottom-color 0.1s;
          vertical-align: middle;
        }
      }
    }
  }
}
</style>
